import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    this.details.addEventListener('toggle', this.#handleToggle, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }

  /**
   * Ensure only one FAQ item is open at a time within the same section.
   * Keeps native behavior elsewhere.
   */
  #handleToggle = () => {
    if (!this.details.open) return;

    const faqSection = this.closest('.shopify-section[id$="__faq"]');
    if (!faqSection) return;

    const accordions = faqSection.querySelectorAll('accordion-custom');
    accordions.forEach((accordion) => {
      if (accordion === this) return;
      const details = accordion.querySelector('details');
      if (details instanceof HTMLDetailsElement) {
        details.open = false;
      }
    });
  };
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}

// FAQ category pills navigation (page.faq)
const faqPillTargets = {
  sistema: '__faq_sistema',
  instalacion: '__faq_instalacion',
  productos: '__faq_productos',
  'uso-mantenimiento': '__faq_uso',
  'envios-devoluciones': '__faq_envios',
  'cuenta-compra': '__faq_cuenta',
};

document.addEventListener('click', (event) => {
  const link = event.target instanceof Element ? event.target.closest('[id$="__faq_nav"] a[href^="#"]') : null;
  if (!(link instanceof HTMLAnchorElement)) return;

  const key = link.getAttribute('href')?.replace('#', '').trim();
  if (!key) return;

  const suffix = faqPillTargets[key];
  if (!suffix) return;

  const targetSection = document.querySelector(`[id$="${suffix}"]`);
  if (!(targetSection instanceof HTMLElement)) return;

  event.preventDefault();

  const headerOffset = Number.parseInt(
    getComputedStyle(document.documentElement).getPropertyValue('--header-group-height') || '0',
    10
  );
  const extraOffset = 24;
  const top = targetSection.getBoundingClientRect().top + window.scrollY - (Number.isNaN(headerOffset) ? 0 : headerOffset) - extraOffset;

  window.scrollTo({
    top: Math.max(top, 0),
    behavior: 'smooth',
  });
});
